import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api } from './api'
import Dashboard from './Dashboard.jsx'
import ForgotPassword from './ForgotPassword.jsx'
import Login from './Login.jsx'
import Boundary from './Boundary.jsx'
import InstallPrompt from './InstallPrompt.jsx'
import LanguageOffer from './LanguageOffer.jsx'
import TokenPage from './TokenPage.jsx'
import { SUPPORTED_LANGUAGES } from './i18n'

/**
 * A link from an email lands on /verify or /reset with a token. Read once, at start-up: the token
 * is then dropped from the address bar, so it does not sit in history, in a shared screenshot, or
 * in the referrer of the next request.
 */
function readLink() {
  const path = window.location.pathname.replace(/\/+$/, '')
  const mode = path === '/verify' ? 'verify' : path === '/reset' ? 'reset' : null
  if (!mode) return null

  const token = new URLSearchParams(window.location.search).get('token')
  if (!token) return null

  window.history.replaceState({}, '', '/')
  return { mode, token }
}

export default function App() {
  const { t, i18n } = useTranslation()
  const [state, setState] = useState({ status: 'loading' })
  const [link, setLink] = useState(readLink)
  const [forgot, setForgot] = useState(false)

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        const config = await api.config()
        // An expired or absent cookie is the ordinary case, not a failure.
        const session = await api.me().catch((failure) => {
          if (failure.unauthenticated) return null
          throw failure
        })

        if (!cancelled) setState({ status: 'ready', config, session })
      } catch (failure) {
        if (!cancelled) setState({ status: 'error', message: failure.message })
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [])

  // Signed in, the app owns the whole screen: its own slim bar on top, navigation on the bottom.
  // The centred shell below is only for the way in.
  if (state.status === 'ready' && !link && !forgot && state.session) {
    return (
      <>
        <LanguageOffer />
        <InstallPrompt />
        <Boundary>
          <Dashboard
            session={state.session}
            config={state.config}
            onSignedOut={() => setState({ ...state, session: null })}
            onSessionChanged={(session) => setState({ ...state, session })}
          />
        </Boundary>
      </>
    )
  }

  return (
    <main className="shell">
      <LanguageOffer />
      <InstallPrompt />
      <header className="shell__header">
        <h1 className="brand">
          <img src="/logo.png" alt="Dosegni" />
        </h1>
        <nav aria-label={t('language.label')}>
          <ul className="languages">
            {SUPPORTED_LANGUAGES.map((language) => (
              <li key={language}>
                <button
                  type="button"
                  className={i18n.resolvedLanguage === language ? 'is-active' : ''}
                  aria-current={i18n.resolvedLanguage === language ? 'true' : undefined}
                  onClick={() => i18n.changeLanguage(language)}
                >
                  {t(`language.${language}`)}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </header>

      <p className="tagline">{t('app.tagline')}</p>

      {state.status === 'loading' && <p>{t('status.loading')}</p>}

      {state.status === 'error' && (
        <p className="status status--bad">{t('status.apiDown', { message: state.message })}</p>
      )}

      {state.status === 'ready' && link && (
        <TokenPage mode={link.mode} token={link.token} onDone={() => setLink(null)} />
      )}

      {state.status === 'ready' && !link && forgot && (
        <ForgotPassword onBack={() => setForgot(false)} />
      )}

      {state.status === 'ready' && !link && !forgot && (
        <Login
          config={state.config}
          onSignedIn={(session) => setState({ ...state, session })}
          onForgotPassword={() => setForgot(true)}
        />
      )}
    </main>
  )
}
